import React from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { subnetInfo, dhcpValid } from '@/lib/subnet';
import { HostIpInput } from '@/components/editor/HostIpInput';
import { Plus, Trash2, Network, AlertCircle, Layers } from 'lucide-react';

export const VlanDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const vlans = useStore((s) => s.doc.vlans);
  const zonesEnabled = useStore((s) => !!s.doc.zonesEnabled);
  const zones = useStore((s) => s.doc.zones || []);
  const addVlan = useStore((s) => s.addVlan);
  const updateVlan = useStore((s) => s.updateVlan);
  const deleteVlan = useStore((s) => s.deleteVlan);
  const setZonesEnabled = useStore((s) => s.setZonesEnabled);
  const addZone = useStore((s) => s.addZone);
  const updateZone = useStore((s) => s.updateZone);
  const deleteZone = useStore((s) => s.deleteZone);

  const colCount = zonesEnabled ? 9 : 8;

  const renderRow = (v) => {
    const info = v.network ? subnetInfo(v.network) : null;
    const dhcp = v.network && v.dhcpStart && v.dhcpEnd ? dhcpValid(v.network, v.dhcpStart, v.dhcpEnd) : null;
    return (
      <tr key={v.id} className="border-b border-border/60 align-top" data-testid={`vlan-row-${v.id}`}>
        <td className="px-2 py-2">
          <input
            type="color"
            value={v.color}
            onChange={(e) => updateVlan(v.id, { color: e.target.value })}
            className="h-8 w-9 cursor-pointer rounded border border-border bg-white p-0.5"
            aria-label={t('color')}
          />
        </td>
        <td className="px-2 py-2">
          <Input
            type="number"
            value={v.vlanId}
            onChange={(e) => updateVlan(v.id, { vlanId: parseInt(e.target.value, 10) || 0 })}
            className="h-8 w-20 font-mono"
          />
        </td>
        <td className="px-2 py-2">
          <Input value={v.name} onChange={(e) => updateVlan(v.id, { name: e.target.value })} className="h-8 w-32" />
        </td>
        {zonesEnabled && (
          <td className="px-2 py-2">
            <Select
              value={v.zoneId || 'none'}
              onValueChange={(val) => updateVlan(v.id, { zoneId: val === 'none' ? null : val })}
            >
              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`vlan-zone-select-${v.id}`}>
                <SelectValue placeholder={t('no_zone')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('no_zone')}</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.id}>
                    {z.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </td>
        )}
        <td className="px-2 py-2">
          <Input
            value={v.network}
            onChange={(e) => updateVlan(v.id, { network: e.target.value })}
            placeholder="192.168.10.0/24"
            className={`h-8 w-44 font-mono ${v.network && (!info || !info.valid) ? 'border-[#B42318] text-[#B42318]' : ''}`}
          />
        </td>
        <td className="px-2 py-2">
          {info && info.valid ? (
            <div className="font-mono text-[11px] leading-snug text-muted-foreground">
              <div>{t('usable_hosts')}: {info.usable}</div>
              <div>{info.firstHost} – {info.lastHost}</div>
              <div>BC {info.broadcast}</div>
            </div>
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-2 py-2">
          <HostIpInput
            cidr={v.network}
            value={v.dhcpStart}
            onChange={(val) => updateVlan(v.id, { dhcpStart: val })}
            placeholder={t('dhcp_host_placeholder')}
            className="w-40"
            testid={`vlan-dhcp-start-${v.id}`}
          />
        </td>
        <td className="px-2 py-2">
          <HostIpInput
            cidr={v.network}
            value={v.dhcpEnd}
            onChange={(val) => updateVlan(v.id, { dhcpEnd: val })}
            placeholder={t('dhcp_host_placeholder')}
            invalid={!!(dhcp && !dhcp.valid)}
            className="w-40"
            testid={`vlan-dhcp-end-${v.id}`}
          />
          {dhcp && !dhcp.valid && (
            <span className="mt-1 flex items-center gap-1 text-[10px] text-[#B42318]">
              <AlertCircle className="h-3 w-3" /> {t('dhcp_range')}
            </span>
          )}
          {dhcp && dhcp.valid && (
            <span className="mt-1 block text-[10px] text-[#0F7B6C]">{dhcp.count} IP</span>
          )}
        </td>
        <td className="px-2 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-[#B42318]"
            onClick={() => deleteVlan(v.id)}
            aria-label={t('delete')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </td>
      </tr>
    );
  };

  const groupHeader = (label, count, key) => (
    <tr key={key} className="bg-muted/40" data-testid={`vlan-group-${key}`}>
      <td colSpan={colCount} className="px-2 py-1.5">
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Layers className="h-3.5 w-3.5" /> {label}
          <span className="rounded bg-background px-1.5 py-0.5 tabular-nums text-[10px] text-muted-foreground">{count}</span>
        </span>
      </td>
    </tr>
  );

  // Group rows by zone when zones are enabled: one section per zone (in the
  // zone list order) plus a trailing "no zone" section for unassigned VLANs.
  const renderBody = () => {
    if (!zonesEnabled) return vlans.map(renderRow);
    const rows = [];
    zones.forEach((z) => {
      const zv = vlans.filter((v) => v.zoneId === z.id);
      rows.push(groupHeader(z.name || t('zone'), zv.length, z.id));
      zv.forEach((v) => rows.push(renderRow(v)));
    });
    const ungrouped = vlans.filter((v) => !v.zoneId || !zones.some((z) => z.id === v.zoneId));
    if (ungrouped.length) {
      rows.push(groupHeader(t('no_zone'), ungrouped.length, 'none'));
      ungrouped.forEach((v) => rows.push(renderRow(v)));
    }
    return rows;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl" data-testid="vlan-table-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" /> {t('manage_vlans')}
          </DialogTitle>
          <DialogDescription>{t('no_vlans_hint')}</DialogDescription>
        </DialogHeader>

        {/* Zones: optional feature, off by default. */}
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="zones-toggle" className="text-sm font-medium">{t('enable_zones')}</Label>
            </div>
            <Switch
              id="zones-toggle"
              checked={zonesEnabled}
              onCheckedChange={setZonesEnabled}
              data-testid="zones-enable-toggle"
            />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{t('zones_hint')}</p>

          {zonesEnabled && (
            <div className="mt-3 space-y-2" data-testid="zones-manager">
              {zones.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('no_zones_hint')}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {zones.map((z) => (
                    <div
                      key={z.id}
                      className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1"
                      data-testid={`zone-row-${z.id}`}
                    >
                      <Input
                        value={z.name}
                        onChange={(e) => updateZone(z.id, { name: e.target.value })}
                        placeholder={t('zone_name_placeholder')}
                        className="h-7 w-40 text-sm"
                        data-testid={`zone-name-${z.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-[#B42318]"
                        onClick={() => deleteZone(z.id)}
                        aria-label={t('delete')}
                        data-testid={`zone-delete-${z.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addZone()}
                className="h-8"
                data-testid="zone-add-button"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> {t('add_zone')}
              </Button>
            </div>
          )}
        </div>

        <div className="max-h-[52vh] overflow-auto">
          {vlans.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-10 text-center">
              <p className="text-sm font-medium">{t('no_vlans_title')}</p>
              <p className="text-xs text-muted-foreground">{t('no_vlans_hint')}</p>
            </div>
          ) : (
            <table className="w-full border-collapse text-sm" data-testid="vlan-table">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-2 font-medium">{t('color')}</th>
                  <th className="px-2 py-2 font-medium">{t('vlan_id')}</th>
                  <th className="px-2 py-2 font-medium">{t('name')}</th>
                  {zonesEnabled && <th className="px-2 py-2 font-medium">{t('zone')}</th>}
                  <th className="px-2 py-2 font-medium">{t('network_cidr')}</th>
                  <th className="px-2 py-2 font-medium">{t('computed')}</th>
                  <th className="px-2 py-2 font-medium">{t('dhcp_from')}</th>
                  <th className="px-2 py-2 font-medium">{t('dhcp_to')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>{renderBody()}</tbody>
            </table>
          )}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={addVlan} data-testid="vlan-add-row-button">
            <Plus className="mr-2 h-4 w-4" /> {t('add_vlan')}
          </Button>
          <Button onClick={() => onOpenChange(false)}>{t('close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
